import React from "react";
import { toDate } from "../../lib/format";
import { dayLabel, timeLabel } from "../../../../utils/eventDateParts";
import Icon from "../../components/Icon";
import Modal, { ModalBody } from "../../components/Modal";
import { Button, Card, Empty, Spinner } from "../../components/primitives";
import ConfirmModal from "../../components/manage/ConfirmModal";
import { ERR_TEXT } from "./manageUtils";
import useOverviewData from "./useOverviewData";

const TILES = [
  ["editbio", "Edit Club Bio", "edit", "var(--gCta)"],
  ["events", "Events", "cal", "linear-gradient(135deg,var(--bright),var(--royal))"],
  ["users", "Members", "users", "var(--gSuccess)"],
  ["applications", "Applications", "userplus", "linear-gradient(135deg,var(--purple),var(--purpleText))"],
  ["share", "Share", "qr", "linear-gradient(135deg,var(--amber),var(--amberText))"],
  ["photos", "Add Photos", "image", "var(--red)"],
];

/** Overview tab (logic in useOverviewData). */
export default function OverviewTab({ club, onTab, onAddPhotos, onPatch }) {
  const {
    state, upcoming, rate, start, broadcast, tile, picker, setPicker,
    confirmEvent, setConfirmEvent, broadcasting, addingPhotos, retry,
  } = useOverviewData({ club, onAddPhotos, onTab, onPatch });

  if (state.loading) return <div className="center-fill"><Spinner large label="Loading overview" /></div>;
  if (state.error) {
    return (
      <Empty icon="alert" title="Couldn't load the overview">
        <div>{ERR_TEXT}</div>
        <Button size="sm" style={{ marginTop: 10 }} onClick={retry} data-testid="manage.overview.retry">Retry</Button>
      </Empty>
    );
  }

  const kpis = [
    ["Members", state.members, "users"],
    ["Upcoming events", state.eventsFailed ? null : upcoming.length, "cal"],
    ["Pending applications", state.pending, "userplus"],
    ...(rate != null ? [["Avg. check-in rate", `${rate}%`, "check"]] : []),
  ];

  return (
    <>
      {club.featured_club === true && club.featured_broadcast_sent !== true && (
        <Card pad className="row wrap mg-banner">
          <span className="gicon"><Icon name="star" /></span>
          <div className="grow">
            <b>You&apos;re Club of the Week!</b>
            <div className="meta">Announce one of your upcoming events to every student.</div>
          </div>
          <Button size="sm" onClick={start} disabled={broadcasting} data-testid="manage.overview.announce">Announce an event</Button>
        </Card>
      )}

      <div className="grid mg-kpis">
        {kpis.map(([label, value, icon]) => (
          <Card key={label} className="kpi">
            <span className="meta row" style={{ gap: 6 }}><Icon name={icon} size={15} />{label}</span>
            <b className="tnum">{value ?? "-"}</b>
          </Card>
        ))}
      </div>

      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span className="label">Manage</span>
      </div>
      <Card>
        {TILES.map(([k, label, icon, g]) => (
          <button key={k} type="button" className="mtile" data-testid={`manage.overview.tile.${k}`} onClick={() => tile(k)} disabled={k === "photos" && addingPhotos}>
            <span className="gicon" style={{ background: g }}><Icon name={icon} /></span>
            <b className="grow">{label}</b>
            {k === "applications" && state.pending > 0 && <span className="unread">{state.pending}</span>}
            {k === "photos" && addingPhotos ? <Spinner label="Uploading" /> : <Icon name="right" size={16} />}
          </button>
        ))}
      </Card>

      {picker && !confirmEvent && (
        <Modal title="Announce which event?" size="narrow" onClose={() => setPicker(false)} testId="manage.overview.announcePicker">
          <ModalBody>
            {upcoming.map((e) => {
              const d = toDate(e.start_date);
              return (
                <button key={e.id} type="button" className="mg-pick" onClick={() => setConfirmEvent(e)} data-testid={`manage.overview.announcePicker.${e.id}`}>
                  <b>{e.title}</b>
                  {d && <span className="meta">{dayLabel(d)} · {timeLabel(d)}</span>}
                </button>
              );
            })}
          </ModalBody>
        </Modal>
      )}
      {confirmEvent && (
        <ConfirmModal
          title="Announce to all students?"
          confirmLabel="Announce"
          busy={broadcasting}
          testId="manage.overview.announceConfirm"
          onConfirm={broadcast}
          onClose={() => setConfirmEvent(null)}
        >
          This sends a push notification to VibeLink students who have notifications enabled about &ldquo;{confirmEvent.title}&rdquo;. This can only be done once per week as Club of the Week.
        </ConfirmModal>
      )}
    </>
  );
}
